"use client";

import { prefetchRecommendations } from "@pamperedchef/consumer-components/product";
import { useEffect } from "react";

import { getProductsBySkus } from "@/app/actions/product";
import {
	PC_ADD_TO_CART_EVENT,
	PC_ADD_TO_CART_RESULT_EVENT,
	isAddToCartEventDetail,
	type AddToCartEventDetail,
	type AddToCartResultDetail
} from "@/app/components/shared/recommended-products/add-to-cart-event";
import { useSessionStore } from "@/app/store";
import { type AddedProductSnapshot } from "@/app/store/cart/add-to-cart-slider-store";
import { runAddToCart } from "@/app/hooks/useAddToCart/run-add-to-cart";

/**
 * The "event door" into add-to-cart, for surfaces that can't call React
 * directly — external bundles like `consumer-web-elements` (Shadow-DOM web
 * components). They dispatch a `pc:add-to-cart` window event; this single,
 * always-mounted listener runs the SAME `runAddToCart` flow the native
 * `useAddToCart` hook uses. It is not a separate cart — just the bridge for
 * non-React callers.
 *
 * Native React surfaces (PDP button, in-tree composites) skip this and call
 * `useAddToCart` directly. Mounted once in the root layout.
 */
export function CartEventBridge(): null {
	useEffect(() => {
		function reportResult(
			detail: AddToCartEventDetail,
			status: AddToCartResultDetail["status"]
		): void {
			if (!detail.requestId) return;
			window.dispatchEvent(
				new CustomEvent<AddToCartResultDetail>(
					PC_ADD_TO_CART_RESULT_EVENT,
					{ detail: { requestId: detail.requestId, status } }
				)
			);
		}

		async function handleAddToCart(event: Event): Promise<void> {
			const detail = (event as CustomEvent).detail;
			if (!isAddToCartEventDetail(detail)) return;

			const locale = useSessionStore.getState().sessionState?.locale;
			if (locale) {
				prefetchRecommendations({
					source: "ATC",
					sku: detail.productCode,
					locale
				});
			}

			const result = await runAddToCart({
				request: {
					productCode: detail.productCode,
					quantity: detail.quantity,
					purchaseType: detail.purchaseType
				},
				source: detail.source,
				product: () => findProduct(detail)
			});

			reportResult(
				detail,
				result.status === "success" ? "success" : "error"
			);
		}

		window.addEventListener(PC_ADD_TO_CART_EVENT, handleAddToCart);
		return () => {
			window.removeEventListener(PC_ADD_TO_CART_EVENT, handleAddToCart);
		};
	}, []);

	return null;
}

async function findProduct(
	detail: AddToCartEventDetail
): Promise<AddedProductSnapshot | undefined> {
	const result = await getProductsBySkus([detail.productCode]);
	const product =
		result.status === "success" ? result.products[0] : undefined;
	if (!product) return undefined;

	return {
		sku: product.sku,
		quantity: Number(detail.quantity) || 1,
		name: product.name,
		imageUrl: product.image || detail.imageUrl || "",
		listPrice: product.listPrice,
		salePrice: product.salePrice
	};
}
